import React from 'react';
import clsx from 'clsx';

import { VALIDATIONS } from '@shared/constants';
import { Dialog, Input, ToastDialog, Checkbox, Grid } from '@shared/components';
import {
  debounce,
  writeXLSFile,
  getJWKS,
  onTokenUpdate,
  getFilteredData,
} from '@utils';

import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import LinearProgress from '@material-ui/core/LinearProgress';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';

import LoadingGif from '@assets/icons/spinner-144x144px.gif';
import SuccessIcon from '@assets/icons/done-green.svg';
import ErrorIcon from '@assets/icons/cross-red.svg';
import AddIcon from '@assets/icons/plus.svg';
import AddDisabledIcon from '@assets/icons/add.svg';
import ClearIcon from '@material-ui/icons/Clear';
import SystemUpdateAltIcon from '@material-ui/icons/SystemUpdateAlt';
import WarningIcon from '@assets/icons/error.svg';

import { SipService, RingCentralService } from '@consumers/services';
import useStyles from './style';

import AudioCodesComponent from './audio-codes';
import FilterComponent from './filter';
import { Tooltip } from '@material-ui/core';

const paperElevation = 1;
let accountId = null;
const numericRegExp = /^\d+$/;
const noop = () => {};
let isProgressBarOpen = false;
const TAB_LIST = [
  { id: '1', name: 'SIP' },
  { id: '2', name: 'Audio Codes' },
];
const defaultState = {
  uid: '',
  accountName: null,
  errorList: {},
  isFetching: false,
  isLoading: false,
  isErrorEncountered: false,
  isUidIncorrect: false,
  showToastDialog: false,
  toastActions: <></>,
  toastIcon: null,
  toastDialogContent: '',
  toastDialogHeading: '',
  downloadForTeams: false,
  isRunningReport: false,
  isRateLimited: false,
  isExportingReport: false,
  isFiltering: false,
  teamsInfo: [],
  extensions: [],
  sites: [],
  devices: [],
  deviceTypes: [],
  deviceNicknames: [],
  selectedFilters: [],
  availableDevice: [],
  sheetData: [],
  isDownloadReady: false,
  showProgessBar: false,
  sortObj: null,
  pageSize: 20,
  pageNumber: 1,
  jwksToken: null,
  rateLimitDelay: 0,
  currentTab: TAB_LIST[0]?.id,
  entries: [],
  gatewayModelSelected: '',
  audioPageSize: 20,
  audioPageNumber: 1,
};

const SipViewer = () => {
  const classes = useStyles();
  const [state, setState] = React.useState(defaultState);

  //dATA FETCHERS
  const fetchSiteList = async () => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
    }));
    const response = await RingCentralService.getAllSites(
      state.uid,
      state.jwksToken
    );
    handleJwksResponse(response, data => {
      const sitesArray = data.map((item, index) => {
        return {
          key: Math.random(),
          label: item.name,
          value: item.name,
          isSiteType: true,
        };
      });
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        sites: sitesArray,
      }));
    });
  };

  const fetchDeviceList = async () => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
    }));
    const response = await RingCentralService.getAllDevices(
      state.uid,
      state.jwksToken
    );
    handleJwksResponse(response, data => {
      let tempArr = [];
      let deviceTypesArray = [];
      let deviceNicknamesArray = [];
      let tempDeviceArray1 = [];
      let tempDeviceArray2 = [];
      data.forEach(device => {
        tempArr.push(device);
        if (!tempDeviceArray1.includes(device.type)) {
          tempDeviceArray1.push(device.type);
          deviceTypesArray.push({
            key: Math.random(),
            label: device.type,
            value: device.type,
            isDeviceType: true,
          });
        }
        if (!tempDeviceArray2.includes(device.name)) {
          tempDeviceArray2.push(device.name);
          deviceNicknamesArray.push({
            key: Math.random(),
            label: device.name,
            value: device.name,
            isNicknameType: true,
          });
        }
      });
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        devices: tempArr,
        availableDevice: tempArr,
        deviceTypes: deviceTypesArray,
        deviceNicknames: deviceNicknamesArray,
      }));
    });
  };

  const fetchExtensionList = async () => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
    }));
    const response = await RingCentralService.getAllExtensions(
      state.uid,
      state.jwksToken
    );
    handleJwksResponse(response, data => {
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        extensions: data,
      }));
    });
  };

  const fetchAccountName = async () => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
    }));
    const { data, error } = await SipService.get(state.uid?.trim());

    if (error) {
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        isErrorEncountered: true,
        isUidIncorrect: true,
        showToastDialog: true,
        toastDialogContent: `Alert: ${
          Array.isArray(error) ? error[0].message : error
        }`,
        toastDialogHeading: 'UID Not Found',
        toastIcon: WarningIcon,
        toastActions: (
          <div className='mt-5 mb-5'>
            <Button
              variant='contained'
              color='primary'
              className='bold-font'
              onClick={() => {
                setState(prevState => ({
                  ...prevState,
                  toastIcon: defaultState.toastIcon,
                  toastActions: defaultState.toastActions,
                  showToastDialog: defaultState.showToastDialog,
                  toastDialogHeading: defaultState.toastDialogHeading,
                  toastDialogContent: defaultState.toastDialogContent,
                  isErrorEncountered: defaultState.isErrorEncountered,
                }));
              }}
            >
              Close and Continue
            </Button>
          </div>
        ),
      }));
    } else {
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        accountName: data?.account?.name,
      }));
    }
  };

  //HANDLERS
  const handleGatewayChange = React.useCallback((event, selectedOption) => {
    const rows = new Array(selectedOption?.ports)
      .fill(null)
      .map((row, index) => {
        return {
          portNumber: `PORT ${index + 1}`,
          extension: '',
          id: index + 1,
        };
      });
    setState(prevState => ({
      ...prevState,
      gatewayModelSelected: selectedOption,
      audioPageNumber: defaultState.audioPageNumber,
      audioPageSize: defaultState.audioPageSize,
      entries: rows,
    }));
  }, []);

  const handleExtensionChange = React.useCallback(
    (selectedOption, row) => {
      let updatedEntries = [...state.entries].map(item => {
        return {
          ...item,
          extension: row.id === item.id ? selectedOption : item.extension,
        };
      });
      setState(prevState => ({
        ...prevState,
        entries: updatedEntries,
      }));
    },
    [state.entries]
  );

  const renderTabContent = tab => {
    if (tab === '1') {
      return (
        <Paper className='mr-10 ml-10 p-6 mb-2' elevation={paperElevation}>
          <Grid
            classes={{
              container: classes.gridPaper,
            }}
            hasLoader
            sortBy={columns.findIndex(
              column => column.field === state.sortObj?.field
            )}
            sortOrder={state.sortObj?.order}
            isLoading={state.isFetching}
            pageSize={state.pageSize}
            pageNumber={state.pageNumber}
            total={state.sheetData.length}
            columns={columns}
            data={gridData}
            hasSelection={false}
            onPageSizeChange={pageSize =>
              setState(prevState => ({
                ...prevState,
                pageSize,
                pageNumber: 1,
                isFetching: true,
              }))
            }
            onPageNumberChange={pageNumber =>
              setState(prevState => ({
                ...prevState,
                pageNumber,
                isFetching: true,
              }))
            }
            onSort={handleSortChange}
            showRowDivider
          />
        </Paper>
      );
    } else {
      return (
        <AudioCodesComponent
          sheetData={state.sheetData}
          extensionList={state.extensions.filter(ext =>
            state.sheetData.some(
              row => row['Extension Number'] == ext.extensionNumber
            )
          )}
          entries={state.entries}
          gatewayModelSelected={state.gatewayModelSelected}
          pageSize={state.audioPageSize}
          pageNumber={state.audioPageNumber}
          handleExtensionChange={handleExtensionChange}
          handleGatewayChange={handleGatewayChange}
          handlePageSizeChange={pageSize => {
            setState(prevState => ({
              ...prevState,
              audioPageSize: pageSize,
              audioPageNumber: defaultState.audioPageNumber,
            }));
          }}
          handlePageNumberChange={pageNumber => {
            setState(prevState => ({
              ...prevState,
              audioPageNumber: pageNumber,
            }));
          }}
        />
      );
    }
  };

  const getTab = tab => {
    return (
      <Tab
        value={tab.id}
        key={tab.id}
        name={tab.id}
        label={
          <span className='d-flex f-align-center'>
            <Typography
              className='float-left text-bold p-1'
              variant='h6'
              component={'span'}
            >
              {tab.name}
            </Typography>
          </span>
        }
      />
    );
  };

  const handleTabChange = React.useCallback(event => {
    const clickedTabId = event.currentTarget.name;
    if (clickedTabId) {
      setState(prevState => ({
        ...prevState,
        currentTab: clickedTabId,
      }));
    }
  }, []);

  const handleSortChange = React.useCallback((field, order) => {
    let sortObj = order ? { field, order } : null;
    setState(prevState => ({
      ...prevState,
      isFetching: true,
    }));
    debounce(sort =>
      setState(prevState => ({
        ...prevState,
        sortObj: sort,
      }))
    )(sortObj);
  }, []);

  const handleExport = () => {
    let dataToBeWrite = state.downloadForTeams
      ? state.teamsInfo
      : state.sheetData;
    if (!dataToBeWrite.length) {
      return setState(prevState => ({
        ...prevState,
        isErrorEncountered: true,
        showToastDialog: true,
        toastDialogContent: `Error: No results found for filter parameters`,
        toastDialogHeading: 'Report Error',
      }));
    }
    let fileExtension = 'xlsx';

    if (state.downloadForTeams) {
      fileExtension = 'csv';
    }

    writeXLSFile(
      `SIP-Provisioning-Info-${state.uid}.${fileExtension}`,
      'sheet',
      dataToBeWrite,
      {
        bookType: fileExtension,
        raw: false,
      }
    );
  };

  const prepareBatches = data => {
    let batches = [];
    const DEVICE_BATCH_LIMIT = 15;
    for (
      let batchIndex = 1;
      batchIndex <= Math.ceil(data.length / DEVICE_BATCH_LIMIT);
      batchIndex += 1
    ) {
      batches.push(
        (
          data.slice(
            (batchIndex - 1) * DEVICE_BATCH_LIMIT,
            batchIndex * DEVICE_BATCH_LIMIT
          ) || []
        ).map(row => ({ ...row, batchIndex }))
      );
    }

    return batches;
  };

  const downloadBatches = async batches => {
    let tempSheetData = state.sheetData;
    let tempTeamsArr = state.teamsInfo;
    let rowInfo = {};
    let teamsRowInfo = {};
    return new Promise(async resolve => {
      const responses = [];
      if (batches && batches.length === 0) {
        resolve(responses);
      }
      for (let index = 0; index < batches.length; index += 1) {
        let deviceExtension = null;
        if (!isProgressBarOpen) {
          setState(prevState => ({
            ...prevState,
            sheetData: [],
            teamsInfo: [],
            isRunningReport: false,
          }));
          break;
        }
        let record = batches[index];
        if (record.extension) {
          deviceExtension = state.extensions.find(element => {
            return (
              record.extension.extensionNumber &&
              element.extensionNumber === record.extension.extensionNumber
            );
          });
        }
        const response = await RingCentralService.getSipDevice(
          state.uid,
          record.id,
          state.jwksToken,
          rateLimit => {
            const { delay } = rateLimit;
            setState(prevState => ({
              ...prevState,
              isRateLimited: true,
              rateLimitDelay: Math.ceil(delay / 1000),
            }));
            const rateLimitDelayInterval = setInterval(() => {
              setState(prevState => {
                const rateLimitDelay =
                  prevState.rateLimitDelay > 0
                    ? prevState.rateLimitDelay - 1
                    : prevState.rateLimitDelay;
                return { ...prevState, rateLimitDelay };
              });
            }, 1000);

            setTimeout(() => {
              clearInterval(rateLimitDelayInterval);
              setState(prevState => ({
                ...prevState,
                isRateLimited: false,
              }));
            }, delay);
          }
        );
        if (response.data) {
          teamsRowInfo = {
            SIPUsername: response?.data?.userName || '-',
            Name: deviceExtension?.name ? deviceExtension.name : '-',
            Email: deviceExtension
              ? deviceExtension.contact
                ? deviceExtension.contact.email
                : '-'
              : '-',
            PhoneNumber:
              record.phoneLines?.length > 0
                ? record.phoneLines[0]?.phoneInfo
                  ? record.phoneLines[0]?.phoneInfo.phoneNumber
                  : '-'
                : '-',
            AuthUsername: response?.data?.authorizationId || '-',
            Password: response.data?.password || '-',
            ExternalId: deviceExtension?.extensionNumber
              ? deviceExtension.extensionNumber
              : '-',
          };
          rowInfo = {
            Serial: record.Serial || record.serial || '-',
            'Device Type': record.type || record?.['Device Type'] || '-',
            'Extension Number': deviceExtension?.extensionNumber
              ? parseInt(deviceExtension.extensionNumber)
              : '-',
            'Extension Type': deviceExtension?.type
              ? deviceExtension.type
              : '-',
            'Extension Name': deviceExtension?.name
              ? deviceExtension.name
              : '-',
            'Extension Email': deviceExtension
              ? deviceExtension.contact
                ? deviceExtension.contact.email
                : '-'
              : '-',
            'Extension Status': deviceExtension ? deviceExtension.status : '-',
            'Extension Site': deviceExtension
              ? deviceExtension.site
                ? deviceExtension.site.name
                : '-'
              : '-',
            'Device Nick Name':
              record?.['Device Nick Name'] || record.name || '-',
            Model: record.Model
              ? record.Model
              : record.model
              ? record.model.name
              : '-',
            Domain: response?.data?.domain || '-',
            'Outbound Proxy': response.data.outboundProxy,
            Username: response?.data?.userName || '-',
            Password: response?.data?.password || '-',
            'Authorization ID': response?.data?.authorizationId || '-',
          };
          tempSheetData.push(rowInfo);
          tempTeamsArr.push(teamsRowInfo);
          setState(prevState => ({
            ...prevState,
            sheetData: tempSheetData,
            teamsInfo: tempTeamsArr,
          }));
          responses.push({ success: true, data: response.data });
        } else {
          responses.push({ success: false, error: response.error });
          resolve(responses);
          break;
        }
        if (index === batches.length - 1) {
          resolve(responses);
        }
      }
    });
  };

  const handleRunExport = async () => {
    let devices = state.availableDevice;
    if (!devices.length) {
      return setState(prevState => ({
        ...prevState,
        isExportingReport: false,
        isErrorEncountered: true,
        showToastDialog: true,
        toastDialogContent: `Error: No results found for filter parameters`,
        toastDialogHeading: 'Report Error',
      }));
    }
    isProgressBarOpen = true;
    setState(prevState => ({
      ...prevState,
      showProgessBar: true,
      isRunningReport: true,
    }));
    const batchRequests = prepareBatches(devices);
    let responses = [];
    for await (let batch of batchRequests) {
      const response = await downloadBatches(batch);
      const err = response.find(item => item.error)?.error;
      if (!isProgressBarOpen) {
        setState(prevState => ({
          ...prevState,
          sheetData: [],
          teamsInfo: [],
          isRunningReport: false,
        }));
        break;
      }
      if (err) {
        setState(prevState => ({
          ...prevState,
          isDownloadReady: !!state.sheetData.length,
          isRunningReport: false,
          showProgessBar: false,
          isErrorEncountered: true,
          showToastDialog: true,
          toastDialogContent: `Error: ${
            Array.isArray(err) ? err[0].message : err
          }`,
          toastDialogHeading: 'Report Error',
        }));
        if (state.sheetData.length) {
          handleExport();
        }
        break;
      }
      responses.push(response);
    }
    responses = responses.flat().filter(response => response);
    if (state.sheetData.length == state.availableDevice.length) {
      isProgressBarOpen = false;
      setState(prevState => ({
        ...prevState,
        isFetching: true,
        isDownloadReady: true,
        isRunningReport: false,
        showProgessBar: defaultState.showProgessBar,
      }));
      handleExport();
    }
  };

  const handleChange = (event, error) => {
    let field = event.currentTarget.name;
    let value = event.currentTarget.value;
    let fieldError = error;
    if (!numericRegExp.test(value)) {
      fieldError = 'Must be a valid number';
    }
    accountId = null;
    setState(prevState => {
      return {
        ...defaultState,
        isExportingReport: false,
        sheetData: [],
        teamsInfo: [],
        selectedFilters: [],
        [field]: value,
        errorList: {
          ...prevState.errorList,
          [field]: fieldError,
        },
      };
    });
  };

  const handleChipDelete = chipToDelete => {
    setState(prevState => {
      const updatedFilters = state.selectedFilters.filter(
        chip =>
          chip.label !== chipToDelete.label ||
          chip.type !== chipToDelete.type ||
          chip.field !== chipToDelete.field
      );
      return {
        ...prevState,
        selectedFilters: updatedFilters,
        availableDevice: getData(state.devices, false, updatedFilters),
      };
    });
  };

  //UTILS
  const getInputAdorment = () => {
    if (state.isLoading) {
      return <img src={LoadingGif} width='26' height='26' />;
    } else if (state.isUidIncorrect) {
      return <img src={ErrorIcon} width='18' height='18' />;
    } else if (!!state.accountName) {
      return <img src={SuccessIcon} width='26' height='26' />;
    } else {
      return null;
    }
  };

  const getChipLabel = chip => {
    let field = 'Site';
    let type = '';
    if (chip.isDeviceType) {
      field = 'Device Type';
    }
    if (chip.isNicknameType) {
      field = 'Device Nick Name';
    }
    if (chip.isAdvancedType) {
      field = `${chip.field}`;
      type =
        chip.type === 'iRegexp'
          ? 'Contains'
          : chip.type === 'iLike'
          ? 'Equals'
          : chip.type === 'notILike'
          ? 'Does not equal'
          : 'Does not contain';
    }
    return `${field}: ${type && type} ${type && ':'} ${
      chip.label || chip.value
    }`;
  };

  const applyAdvancedFilter = (filteredData, advancedFilter) => {
    if (advancedFilter.length) {
      let advancedFilterArray = [];
      let rowInfo = {};
      for (let record of filteredData) {
        let deviceExtension = null;
        if (record.extension) {
          deviceExtension = state.extensions.find(element => {
            return (
              record.extension.extensionNumber &&
              element.extensionNumber === record.extension.extensionNumber
            );
          });
        }
        rowInfo = {
          id: record.id,
          extension: record.extension || '-',
          Serial: record.serial || '-',
          'Device Type': record.type || '-',
          'Extension Number': deviceExtension?.extensionNumber
            ? parseInt(deviceExtension.extensionNumber)
            : '-',
          'Extension Type': deviceExtension?.type ? deviceExtension.type : '-',
          'Extension Name': deviceExtension?.name ? deviceExtension.name : '-',
          'Extension Email': deviceExtension
            ? deviceExtension.contact
              ? deviceExtension.contact.email
              : '-'
            : '-',
          'Extension Status': deviceExtension?.status
            ? deviceExtension.status
            : '-',
          'Extension Site': deviceExtension
            ? deviceExtension.site
              ? deviceExtension.site.name
              : '-'
            : '-',
          'Device Nick Name': record?.name || '-',
          Model: record.model ? record.model.name : '-',
        };

        advancedFilterArray.push(rowInfo);
      }
      advancedFilterArray = getFilteredData(
        advancedFilterArray,
        advancedFilter
      );
      return advancedFilterArray;
    } else {
      return filteredData;
    }
  };

  const getData = (
    dataToFilter = [],
    dataToDisplay = false,
    filters = state.selectedFilters,
    pageSize = state.pageSize,
    pageNumber = state.pageNumber,
    sort = state.sortObj
  ) => {
    let preparedData = dataToFilter;
    if (!preparedData || preparedData.length === 0) {
      return [];
    }
    if (filters.length && !dataToDisplay) {
      const siteFilter = filters
        .filter(filter => filter.isSiteType)
        .map(item => item.value);
      const deviceTypeFilter = filters
        .filter(filter => filter.isDeviceType)
        .map(item => item.value);
      const deviceNameFilter = filters
        .filter(filter => filter.isNicknameType)
        .map(item => item.value);
      const advancedFilter = filters.filter(filter => filter.isAdvancedType);
      const filteredData = preparedData
        .filter(device =>
          siteFilter.length ? siteFilter.includes(device?.site?.name) : true
        )
        .filter(device =>
          deviceTypeFilter.length
            ? deviceTypeFilter.includes(device.type)
            : true
        )
        .filter(device =>
          deviceNameFilter.length
            ? deviceNameFilter.includes(device.name)
            : true
        );
      preparedData = applyAdvancedFilter(filteredData, advancedFilter);
    }

    if (dataToDisplay) {
      let sortObj = sort ? sort : { field: 'Authorization ID', order: 'ASC' };
      preparedData.sort((rowA, rowB) => {
        if (sortObj.field === 'Extension Number') {
          if (sortObj.order === 'ASC') {
            if (isNaN(rowA[sortObj.field]) && isNaN(rowB[sortObj.field])) {
              return 0;
            } else if (
              isNaN(rowA[sortObj.field]) &&
              !isNaN(rowB[sortObj.field])
            ) {
              return -1;
            } else if (
              !isNaN(rowA[sortObj.field]) &&
              isNaN(rowB[sortObj.field])
            ) {
              return 1;
            } else if (
              !isNaN(rowA[sortObj.field]) &&
              !isNaN(rowB[sortObj.field])
            ) {
              return rowA[sortObj.field] - rowB[sortObj.field];
            }
          } else {
            if (isNaN(rowA[sortObj.field]) && isNaN(rowB[sortObj.field])) {
              return 0;
            } else if (
              isNaN(rowA[sortObj.field]) &&
              !isNaN(rowB[sortObj.field])
            ) {
              return 1;
            } else if (
              !isNaN(rowA[sortObj.field]) &&
              isNaN(rowB[sortObj.field])
            ) {
              return -1;
            } else if (
              !isNaN(rowA[sortObj.field]) &&
              !isNaN(rowB[sortObj.field])
            ) {
              return rowB[sortObj.field] - rowA[sortObj.field];
            }
          }
        } else {
          if (sortObj.order === 'ASC') {
            return rowA[sortObj.field].toLowerCase() >
              rowB[sortObj.field].toLowerCase()
              ? 1
              : -1;
          } else {
            return rowB[sortObj.field].toLowerCase() >
              rowA[sortObj.field].toLowerCase()
              ? 1
              : -1;
          }
        }
      });
    }

    if (pageSize !== -1 && dataToDisplay) {
      preparedData = preparedData.slice(
        (pageNumber - 1) * pageSize,
        (pageNumber - 1) * pageSize + pageSize
      );
    }

    return preparedData;
  };

  //COLUMNS

  const columns = [
    {
      field: 'Serial',
      label: 'SERIAL',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['Serial'] != '-' ? row['Serial'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Serial']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Device Type',
      label: 'DEVICE TYPE',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['Device Type'] != '-' ? row['Device Type'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Device Type']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Extension Number',
      label: 'EXTENSION NUMBER',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={
              row['Extension Number'] != '-' ? row['Extension Number'] : ''
            }
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Extension Number']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Extension Type',
      label: 'EXTENSION TYPE',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['Extension Type'] != '-' ? row['Extension Type'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Extension Type']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Extension Name',
      label: 'EXTENSION NAME',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['Extension Name'] != '-' ? row['Extension Name'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Extension Name']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Extension Email',
      label: 'EXTENSION EMAIL',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['Extension Email'] != '-' ? row['Extension Email'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Extension Email']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Extension Status',
      label: 'EXTENSION STATUS',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={
              row['Extension Status'] != '-' ? row['Extension Status'] : ''
            }
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Extension Status']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Extension Site',
      label: 'EXTENSION SITE',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['Extension Site'] != '-' ? row['Extension Site'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Extension Site']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Device Nick Name',
      label: 'DEVICE NICK NAME',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={
              row['Device Nick Name'] != '-' ? row['Device Nick Name'] : ''
            }
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Device Nick Name']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Model',
      label: 'MODEL',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['Model'] != '-' ? row['Model'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Model']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Domain',
      label: 'DOMAIN',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['Domain'] != '-' ? row['Domain'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Domain']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Outbound Proxy',
      label: 'OUTBOUND PROXY',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['Outbound Proxy'] != '-' ? row['Outbound Proxy'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Outbound Proxy']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Username',
      label: 'USERNAME',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['Username'] != '-' ? row['Username'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Username']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Password',
      label: 'PASSWORD',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['Password'] != '-' ? row['Password'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Password']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Authorization ID',
      label: 'AUTHORIZATION ID',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={
              row['Authorization ID'] != '-' ? row['Authorization ID'] : ''
            }
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Authorization ID']}
            </Typography>
          </Tooltip>
        );
      },
    },
  ];

  const fetchJwksToken = async accountUid => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
    }));
    let data;
    let error;
    try {
      ({ data, error } = await getJWKS(accountUid));
    } catch (err) {
      error = err?.message || err;
    }
    if (error) {
      const firstError = Array.isArray(error) ? error[0] : undefined;
      const nestedError = firstError?.errors?.[0];
      const isInvalidUid = nestedError?.errorCode === 'OAU-222';
      const errorMessage = isInvalidUid
        ? `Alert: UID not located in Salesforce.`
        : `Error: ${
            nestedError?.message ||
            firstError?.message ||
            (typeof error === 'string' ? error : 'Something went wrong.')
          }`;

      setState(prevState => ({
        ...prevState,
        isLoading: false,
        isErrorEncountered: true,
        isUidIncorrect: true,
        showToastDialog: true,
        toastDialogContent: errorMessage,
        toastDialogHeading: isInvalidUid ? 'UID Not Found' : 'Report Error...',
        toastIcon: WarningIcon,
        toastActions: (
          <div className='mt-5 mb-5'>
            <Button
              variant='contained'
              color='primary'
              className='bold-font'
              onClick={() => {
                setState(prevState => ({
                  ...prevState,
                  toastIcon: defaultState.toastIcon,
                  toastActions: defaultState.toastActions,
                  showToastDialog: defaultState.showToastDialog,
                  toastDialogHeading: defaultState.toastDialogHeading,
                  toastDialogContent: defaultState.toastDialogContent,
                  isErrorEncountered: defaultState.isErrorEncountered,
                }));
              }}
            >
              Close and Continue
            </Button>
          </div>
        ),
      }));
    } else {
      if (data) {
        setState(prevState => ({
          ...prevState,
          isLoading: true,
        }));
        onTokenUpdate(async uid => {
          const _accountUid = uid || accountUid;
          const { data, error } = await getJWKS(_accountUid);
          if (!data) {
            setState(prevState => ({
              ...prevState,
              isLoading: false,
            }));
            return false;
          }
          setState(prevState => ({
            ...prevState,
            jwksToken: data,
            isLoading: false,
          }));
          return data;
        });
      }
      setState(prevState => ({
        ...prevState,
        jwksToken: data,
        isLoading: false,
      }));
    }
  };

  const handleJwksResponse = (response = {}, callBack = noop) => {
    const { data, error, paging, token } = response;

    if (error) {
      if (Array.isArray(error) && error[0].errorCode === 'OAU-213') {
        return false;
      }
    } else {
      callBack(data, error, paging);
      if (token) {
        setState(prevState => ({
          ...prevState,
          jwksToken: token,
        }));
      }
    }
  };

  React.useEffect(() => {
    if (!!state.jwksToken && !state.devices.length) {
      fetchAccountName();
      fetchSiteList();
      fetchDeviceList();
      fetchExtensionList();
    }
  }, [state.jwksToken]);

  const handleApplyFilter = selectedFilters => {
    let updateFilters = state.selectedFilters;
    // let keys = updateFilters.map(item => item.key);
    selectedFilters.map(filter => {
      const isAvailable = updateFilters.some(
        item =>
          item.key === filter.key &&
          item.field === filter.field &&
          item.type === filter.type
      );
      if (!isAvailable) updateFilters.push(filter);
    });
    setState(prevState => ({
      ...prevState,
      isFiltering: false,
      selectedFilters: updateFilters,
      availableDevice: getData(state.devices),
    }));
  };
  React.useEffect(() => {
    setTimeout(() => {
      setState(prevState => ({
        ...prevState,
        isFetching: false,
      }));
    }, 400);
  }, [
    state.pageSize,
    state.pageNumber,
    state.sortObj,
    state.isDownloadReady,
    state.isExportingReport,
  ]);

  const gridData = getData(
    state.sheetData,
    true,
    [],
    state.pageSize,
    state.pageNumber,
    state.sortObj
  );
  return (
    <div>
      <Paper className='p-6 mr-10 ml-10' elevation={paperElevation}>
        <div className='d-flex f-justify-between f-align-center'>
          <div>
            <Typography variant='h5' className='medium-font mb-2'>
              SIP Viewer
            </Typography>
            <Typography variant='caption' className='color-text-secondary'>
              The SIP tool helps us view and download SIP information per
              account, site, or user.
            </Typography>
          </div>
          <div className='d-flex'>
            <div>
              <Button
                className='mr-5 alt-icon-button'
                variant='contained'
                color='primary'
                onClick={() => {
                  if (state.isDownloadReady) {
                    handleExport();
                  } else {
                    setState(prevState => ({
                      ...prevState,
                      isExportingReport: true,
                    }));
                    handleRunExport();
                  }
                }}
                disabled={!state.devices.length || state.isRunningReport}
              >
                <SystemUpdateAltIcon />
              </Button>
            </div>
            <div>
              <Button
                variant='contained'
                color='primary'
                className='bold-font'
                onClick={() => {
                  if (state.isDownloadReady && !state.isExportingReport) {
                    accountId = null;
                    setState({
                      ...defaultState,
                      entries: [],
                      sheetData: [],
                      teamsInfo: [],
                      selectedFilters: [],
                    });
                  } else if (state.isExportingReport) {
                    setState(prevState => ({
                      ...prevState,
                      isFetching: true,
                      isExportingReport: false,
                    }));
                  } else {
                    handleRunExport();
                  }
                }}
                disabled={!state.devices.length || state.isRunningReport}
              >
                {state.isDownloadReady && !state.isExportingReport
                  ? 'New Report'
                  : 'Run Report'}
              </Button>
              {!!state.availableDevice.length && !state.sheetData.length && (
                <Typography
                  variant='body2'
                  className='d-flex f-justify-center bold-font mt-2'
                >
                  {`${state.availableDevice.length} Remaining`}
                </Typography>
              )}
            </div>
          </div>
        </div>
        <Typography variant='body1' className='bold-font mt-4'>
          Enter the UID
        </Typography>
        <div className='d-flex f-align-center'>
          <div className='w-25'>
            <Input
              onKeyDown={event => {
                if (parseInt(event.keyCode) === 13) {
                  if (
                    !event.target.value ||
                    accountId === event.target.value ||
                    !!state.errorList?.uid ||
                    state.jwksToken
                  ) {
                    return;
                  }
                  fetchJwksToken(event.target.value);
                  accountId = event.target.value;
                }
              }}
              type='text'
              placeholder='Target Account (UID)'
              name='uid'
              fullWidth
              className='mb-4'
              value={
                !!state.accountName
                  ? `${state.uid} - ${state.accountName}`
                  : `${state.uid}`
              }
              onChange={(event, error) => {
                handleChange(event, error);
              }}
              hasError={!!state.errorList?.uid}
              helperText={state.errorList?.uid}
              onBlur={event => {
                if (
                  !event.currentTarget.value ||
                  accountId === event.currentTarget.value ||
                  !!state.errorList?.uid ||
                  state.jwksToken
                ) {
                  return;
                }
                fetchJwksToken(event.currentTarget.value);
                accountId = event.currentTarget.value;
              }}
              validations={{
                [VALIDATIONS.MAX_LENGTH]: 19,
                [VALIDATIONS.REQUIRED]: true,
              }}
              InputProps={{
                endAdornment: getInputAdorment(),
              }}
              disabled={
                state.isRunningReport ||
                (state.sheetData.length && !state.isExportingReport)
              }
            />
          </div>
          <div
            className={clsx('d-flex f-align-center', {
              'mb-2': !state.errorList?.uid,
              'mb-8': !!state.errorList?.uid,
            })}
          >
            <Checkbox
              className='mr-1 ml-3'
              name='downloadForTeams'
              checked={state.downloadForTeams}
              onChange={event => {
                if (event.currentTarget) {
                  setState(prevState => {
                    return {
                      ...prevState,
                      downloadForTeams: !prevState.downloadForTeams,
                    };
                  });
                }
              }}
            />
            <Typography variant='body1'>Microsoft Teams Format</Typography>
          </div>
        </div>
      </Paper>
      {!!state.devices.length && (
        <Paper className='mr-10 ml-10 p-6 mt-5' elevation={paperElevation}>
          <div className={classes.filterPaper}>
            <Button
              variant='outlined'
              color='primary'
              className='bold-font mr-4'
              classes={{
                root: classes.filterBtn,
              }}
              onClick={() => {
                setState(prevState => ({
                  ...prevState,
                  isFiltering: true,
                }));
              }}
              disabled={state.sheetData.length}
              startIcon={
                <img
                  src={state.sheetData.length ? AddDisabledIcon : AddIcon}
                  alt='add-filter'
                />
              }
            >
              Add Filter
            </Button>
            {state.selectedFilters.map(chip => {
              return (
                <Chip
                  label={getChipLabel(chip)}
                  color='primary'
                  className={clsx('mr-4 mt-2 mb-2', {
                    [classes.deviceChip]: chip.isDeviceType,
                    [classes.siteChip]: chip.isSiteType,
                    [classes.nicknameChip]: chip.isNicknameType,
                  })}
                  variant={chip.isAdvancedType && 'outlined'}
                  onDelete={() => handleChipDelete(chip)}
                  size='small'
                  disabled={state.sheetData.length}
                  deleteIcon={<ClearIcon />}
                />
              );
            })}
          </div>
        </Paper>
      )}
      {state.showToastDialog && (
        <ToastDialog
          open={state.showToastDialog}
          heading={state.toastDialogHeading}
          contentText={state.toastDialogContent}
          isError={state.isErrorEncountered}
          toastActions={state.toastActions}
          toastIcon={state.toastIcon}
          onClose={() => {
            setState(prevState => ({
              ...prevState,
              showToastDialog: defaultState.showToastDialog,
              toastDialogHeading: defaultState.toastDialogHeading,
              toastDialogContent: defaultState.toastDialogContent,
              isErrorEncountered: defaultState.isErrorEncountered,
              toastActions: defaultState.toastActions,
              toastIcon: defaultState.toastIcon,
            }));
          }}
        />
      )}
      {state.isFiltering && (
        <FilterComponent
          sites={state.sites}
          deviceTypes={state.deviceTypes}
          deviceNicknames={state.deviceNicknames}
          isOpen={state.isFiltering}
          onSave={handleApplyFilter}
          onClose={() => {
            setState(prevState => ({
              ...prevState,
              isFiltering: false,
            }));
          }}
        />
      )}
      {state.showProgessBar && (
        <Dialog
          disableBackdropClick
          disableEscapeKeyDown
          open={state.showProgessBar}
          classes={{
            paper: classes.progressDialog,
          }}
          onClose={() => {
            isProgressBarOpen = false;
            setState(prevState => ({
              ...prevState,
              showProgessBar: false,
              isExportingReport: false,
            }));
          }}
        >
          <Dialog.Title hasClose />
          <Dialog.Content>
            <div className='mt-6'>
              <Typography
                variant='h4'
                className='d-flex f-justify-center bold-font'
              >
                {(
                  state.availableDevice.length - state.sheetData.length
                ).toString()}
              </Typography>
              <Typography
                variant='body1'
                className='d-flex f-justify-center bold-font mb-10'
              >
                REMAINING
              </Typography>
              <LinearProgress
                className='mt-2'
                variant='determinate'
                value={
                  (state.sheetData.length / state.availableDevice.length) * 100
                }
              />
              <Typography variant='body2' className='color-text-secondary mt-1'>
                {`${Math.floor(
                  (state.sheetData.length / state.availableDevice.length) * 100
                )}%`}
              </Typography>
            </div>
            {state.isRateLimited && (
              <div className='d-flex f-align-center f-justify-center'>
                <Chip
                  label={`Oops! Rate limit exceeded. Download will resume in ${
                    state.rateLimitDelay || '60'
                  } seconds.`}
                  color='primary'
                  className='mr-4 mt-2 mb-2 bg-danger color-white'
                />
              </div>
            )}
          </Dialog.Content>
        </Dialog>
      )}
      {state.isDownloadReady && !state.isExportingReport && (
        <>
          <div className='d-flex f-align-center f-justify-between mr-10 ml-10 mt-5'>
            <Tabs value={state.currentTab} onChange={handleTabChange}>
              {TAB_LIST.map(tab => {
                return getTab(tab);
              })}
            </Tabs>
          </div>
          <div> {renderTabContent(state.currentTab)}</div>
        </>
      )}
    </div>
  );
};
export default SipViewer;
